import type { ComponentPropsWithoutRef } from 'react';
import { forwardRef, memo, useEffect, useState } from 'react';

import { cx } from '@/utils/cx';

import { DEFAULT_MAX_BAR_WIDTH_PX, DEFAULT_TOUCH_TARGET_PX, computeMenuToggleVars } from './geometry';
import { MenuToggleBars } from './MenuToggleBars';
import styles from './MenuToggleButton.module.scss';

export interface MenuToggleButtonProps extends Omit<ComponentPropsWithoutRef<'button'>, 'children'> {
  /** `aria-label`로 사용됨. 커스텀 `aria-label` prop으로 오버라이드 가능. */
  label: string;
  /** 버튼(정사각형) 한 변의 길이(px). 12~96으로 clamp. */
  touchTargetPx?: number;
  toggled?: boolean;
  /** 비제어형 초기 토글 상태 (기본 `false`). `toggled`와 동시에 사용 불가. */
  defaultToggled?: boolean;
  /** 한 줄 막대의 가로 길이 상한(px). 기본 32. */
  maxBarWidthPx?: number;
  /** 토글 상태 변경 시 호출. 제어형: 호스트가 `toggled`를 업데이트해야 함. 비제어형: 내부 상태 자동 전환 후 알림. */
  onToggle?: (toggled: boolean) => void;
}

export const MenuToggleButton = memo(
  forwardRef<HTMLButtonElement, MenuToggleButtonProps>(function MenuToggleButton(
    {
      label,
      touchTargetPx: touchTargetProp = DEFAULT_TOUCH_TARGET_PX,
      toggled,
      defaultToggled,
      disabled = false,
      maxBarWidthPx: maxBarWidthProp = DEFAULT_MAX_BAR_WIDTH_PX,
      onToggle,
      className,
      onClick,
      style,
      'aria-label': ariaLabel,
      ...rest
    },
    ref,
  ) {
    const [internalToggled, setInternalToggled] = useState(defaultToggled ?? false);

    const isToggled = toggled !== undefined ? toggled : internalToggled;

    useEffect(() => {
      if (process.env.NODE_ENV === 'production') return;
      if (toggled !== undefined && defaultToggled !== undefined) {
        console.warn(
          '[MenuToggleButton] toggled과 defaultToggled을 동시에 사용할 수 없습니다. toggled(제어형)이 우선 적용됩니다.',
        );
      }
    }, [toggled, defaultToggled]);

    const handler = (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      if (e.defaultPrevented) return;

      const next = !isToggled;
      if (toggled === undefined) {
        setInternalToggled(next);
      }
      onToggle?.(next);
    };

    const cssVars = computeMenuToggleVars(touchTargetProp, maxBarWidthProp);

    return (
      <button
        {...rest}
        aria-expanded={isToggled}
        aria-label={ariaLabel ?? label}
        className={cx(styles.container, className)}
        disabled={disabled}
        onClick={handler}
        ref={ref}
        style={{ ...style, ...cssVars }}
        type="button"
      >
        <MenuToggleBars toggled={isToggled} />
      </button>
    );
  }),
);

MenuToggleButton.displayName = 'MenuToggleButton';
